import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { Upload, X, Check, AlertTriangle, FileSpreadsheet } from 'lucide-react';
import { Pharmacy } from '../types';
import { adminFetch } from '../api';

interface OriginalExcelUploaderProps {
  onClose: () => void;
  onUploadSuccess: (message: string) => void;
}

export const OriginalExcelUploader: React.FC<OriginalExcelUploaderProps> = ({
  onClose,
  onUploadSuccess
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parsedData, setParsedData] = useState<{ day: string; dayNum: number; pharmacies: string[] }[] | null>(null);
  const [catalog, setCatalog] = useState<Pharmacy[]>([]);
  const [missingPharmacies, setMissingPharmacies] = useState<Set<string>>(new Set());
  const [missingDays, setMissingDays] = useState<number[]>([]);
  const [emptyDays, setEmptyDays] = useState<number[]>([]);
  const [askHeaderOverride, setAskHeaderOverride] = useState(false);
  const [forceHeaderOverride, setForceHeaderOverride] = useState(false);
  const [currentHeader, setCurrentHeader] = useState<string>("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState((now.getMonth() + 1).toString().padStart(2, "0"));
  const [selectedYear, setSelectedYear] = useState(now.getFullYear().toString());

  useEffect(() => {
    fetch('/api/pharmacies')
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          setCatalog(data);
        } else if (data && data.pharmacies) {
          setCatalog(data.pharmacies);
        }
      })
      .catch(err => console.error("Error loading pharmacies", err));
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setError(null);
      setParsedData(null);
    }
  };

  const normalizeText = (text: string) => {
    return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
  };

  const processFile = async (override = forceHeaderOverride) => {
    if (!file) return;
    setLoading(true);
    setError(null);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 }) as any[][];

      // Find the header row
      let headerRowIndex = -1;
      let firstDataRowIndex = -1;
      for (let i = 0; i < rows.length; i++) {
        if (rows[i] && rows[i].length > 0) {
          if (firstDataRowIndex === -1) firstDataRowIndex = i;
          const firstCell = String(rows[i][0] || '').toLowerCase().trim();
          if (firstCell === 'dia' || firstCell === 'día' || firstCell === 'day') {
            headerRowIndex = i;
            break;
          }
        }
      }

      if (headerRowIndex === -1 && !override) {
        const actualHeaderRow = firstDataRowIndex !== -1 ? rows[firstDataRowIndex] : [];
        const formattedActual = Array.from({ length: 5 }).map((_, i) => {
          return actualHeaderRow[i] ? String(actualHeaderRow[i]) : `Unnamed: ${i}`;
        }).join(", ");
        setCurrentHeader(formattedActual);
        setAskHeaderOverride(true);
        setLoading(false);
        return;
      }

      if (headerRowIndex === -1 && override) {
        headerRowIndex = firstDataRowIndex; // Treat the first actual row as the bad header and skip it
      }

      // Do NOT filter out rows because that ruins the groups of 5.
      // Pandas iloc[i:i+5] takes strictly 5 consecutive rows from the data, even if some cells are blank.
      const rawDataRows = rows.slice(headerRowIndex + 1);

      let results: { day: string; dayNum: number; pharmacies: string[] }[] = [];

      // Process in groups of 5 as specified
      for (let i = 0; i < rawDataRows.length; i += 5) {
        const group = rawDataRows.slice(i, i + 5);
        if (group.length === 0) continue;
        
        // Ensure we actually have data in the group, skip trailing empty groups
        const hasAnyData = group.some(r => r && r.length > 0);
        if (!hasAnyData) continue;

        // Pharmacies are on the first row of the group, columns 1 to 4 (B to E)
        const pharmacies: string[] = [];
        for (let j = 1; j <= 4; j++) {
          const p = group[0]?.[j];
          if (p) {
            pharmacies.push(String(p).trim());
          }
        }

        for (const row of group) {
          if (!row || !row[0]) continue; // Skip if no day cell
          
          const dayStr = String(row[0]).trim();
          if (!dayStr) continue;

          // Extract day number using regex
          const match = dayStr.match(/(\d{1,2})$/);
          const dayNum = match ? parseInt(match[1], 10) : 0;

          if (dayNum > 0) {
            results.push({
              day: dayStr,
              dayNum,
              pharmacies: [...pharmacies]
            });
          }
        }
      }

      // Sort by day number
      results.sort((a, b) => a.dayNum - b.dayNum);

      // Validation
      const daysInMonth = new Date(parseInt(selectedYear), parseInt(selectedMonth), 0).getDate();
      
      const missing = [];
      const empty = [];
      const notInCatalog = new Set<string>();

      const catalogNames = catalog.map(p => {
        // Strip everything that isn't a letter or a number for the most robust comparison
        return normalizeText(p.nombre).replace(/[^a-z0-9]/g, '');
      });

      for (let d = 1; d <= daysInMonth; d++) {
        const found = results.find(r => r.dayNum === d);
        if (!found) {
          missing.push(d);
        } else if (found.pharmacies.length === 0) {
          empty.push(d);
        }
      }

      results.forEach(r => {
        r.pharmacies.forEach(p => {
          const rawNormalized = normalizeText(p);
          const ultraNormalized = rawNormalized.replace(/[^a-z0-9]/g, '');
          
          const exists = catalogNames.some(c => 
            c === ultraNormalized || 
            (c.length > 3 && ultraNormalized.includes(c)) || 
            (ultraNormalized.length > 3 && c.includes(ultraNormalized))
          );
          
          if (!exists) {
            notInCatalog.add(p);
          }
        });
      });

      setMissingDays(missing);
      setEmptyDays(empty);
      setMissingPharmacies(notInCatalog);
      setParsedData(results);
    } catch (err: any) {
      setError(err.message || "Error al procesar el archivo Excel");
    } finally {
      setLoading(false);
    }
  };

  const uploadParsedData = async () => {
    if (!parsedData) return;
    setLoading(true);
    
    try {
      // Build CSV
      let csvContent = "dia-inicio,farmacia\n";
      parsedData.forEach(row => {
        row.pharmacies.forEach(p => {
          // Format as "LUN 01" so backend can parse it, or just use the day string
          // The backend expects day-inicio to have the day number and some text.
          csvContent += `"${row.day}","${p}"\n`;
        });
      });

      // Add BOM
      const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
      const formData = new FormData();
      formData.append("file", blob, "upload.csv");
      formData.append("month", selectedMonth);
      formData.append("year", selectedYear);
      formData.append("skipPharmacyUpdate", "true");

      const res = await adminFetch("/api/upload", {
        method: "POST",
        body: formData,
      });

      const resData = await res.json();
      if (res.ok) {
        onUploadSuccess(resData.message || "Cronograma actualizado correctamente");
        onClose();
      } else {
        throw new Error(resData.error || "Error al subir cronograma");
      }
    } catch (err: any) {
      setError(err.message || "Error al subir cronograma");
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b border-slate-100 bg-slate-50">
          <h2 className="text-sm font-extrabold text-slate-800 uppercase tracking-wide flex items-center gap-2">
            <FileSpreadsheet size={18} className="text-emerald-500" />
            Carga de Archivo Original (Colfarma)
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 bg-white rounded-full p-1 border border-slate-200">
            <X size={16} />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1 bg-slate-50">
          {askHeaderOverride ? (
            <div className="space-y-4">
              <div className="p-5 bg-white rounded-xl border border-slate-300 text-slate-800 text-left shadow-sm">
                <h3 className="font-bold text-sm mb-4 text-slate-800">Encabezados no válidos</h3>
                <p className="text-sm mb-4">
                  El encabezado del archivo no es el esperado.
                </p>
                <p className="text-xs font-mono text-slate-600 mb-2">
                  <span className="font-bold text-slate-800">Encabezado actual:</span> {currentHeader}
                </p>
                <p className="text-xs font-mono text-slate-600 mb-6">
                  <span className="font-bold text-slate-800">Encabezado esperado:</span> dia, farmacia 1, farmacia 2, farmacia 3, farmacia 4
                </p>
                <p className="text-sm mb-4">
                  ¿Desea modificarlo?
                </p>
                <div className="flex justify-end gap-3 mt-4">
                  <button
                    onClick={() => {
                      setAskHeaderOverride(false);
                      setForceHeaderOverride(false);
                    }}
                    className="px-4 py-2 bg-slate-100 border border-slate-300 text-slate-700 font-bold text-xs rounded-lg hover:bg-slate-200 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => {
                      setAskHeaderOverride(false);
                      setForceHeaderOverride(true);
                      setLoading(true);
                      // Pasamos el nuevo valor directamente
                      processFile(true);
                    }}
                    disabled={loading}
                    className="px-4 py-2 bg-blue-500 text-white font-bold text-xs rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
                  >
                    {loading ? "Procesando..." : "Sí, modificar"}
                  </button>
                </div>
              </div>
            </div>
          ) : !parsedData ? (
            <div className="space-y-4">
              <p className="text-xs text-slate-500">
                Selecciona el archivo Excel original (el que agrupa en bloques de 5 días) para el mes <strong>{selectedMonth}/{selectedYear}</strong>.
              </p>
              
              <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center bg-white">
                <input
                  type="file"
                  accept=".xlsx, .xls"
                  onChange={handleFileChange}
                  className="hidden"
                  id="original-excel-upload"
                />
                <label
                  htmlFor="original-excel-upload"
                  className="flex flex-col items-center justify-center cursor-pointer text-slate-600 hover:text-emerald-600 transition-colors"
                >
                  <Upload size={32} className="mb-2" />
                  <span className="font-bold text-sm">Seleccionar Archivo Excel</span>
                  {file && <span className="text-xs mt-2 text-emerald-600">{file.name}</span>}
                </label>
              </div>

              {error && (
                <div className="p-3 bg-red-50 text-red-700 text-xs rounded-lg border border-red-200">
                  {error}
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={processFile}
                  disabled={!file || loading}
                  className="px-4 py-2 bg-emerald-500 text-white font-bold text-xs rounded-lg hover:bg-emerald-600 disabled:opacity-50 transition-colors"
                >
                  {loading ? "Procesando..." : "Analizar Archivo"}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className={`p-3 rounded-xl border flex flex-col items-center justify-center text-center ${missingDays.length === 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                  <span className={`text-2xl font-black ${missingDays.length === 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {missingDays.length}
                  </span>
                  <span className={`text-[10px] uppercase font-bold tracking-wide ${missingDays.length === 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    Días Faltantes
                  </span>
                </div>
                <div className={`p-3 rounded-xl border flex flex-col items-center justify-center text-center ${emptyDays.length === 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                  <span className={`text-2xl font-black ${emptyDays.length === 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {emptyDays.length}
                  </span>
                  <span className={`text-[10px] uppercase font-bold tracking-wide ${emptyDays.length === 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                    Días Sin Farmacias
                  </span>
                </div>
                <div className={`p-3 rounded-xl border flex flex-col items-center justify-center text-center ${missingPharmacies.size === 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                  <span className={`text-2xl font-black ${missingPharmacies.size === 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {missingPharmacies.size}
                  </span>
                  <span className={`text-[10px] uppercase font-bold tracking-wide ${missingPharmacies.size === 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                    No en Catálogo
                  </span>
                </div>
              </div>

              {(missingDays.length > 0 || emptyDays.length > 0 || missingPharmacies.size > 0) && (
                <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 text-amber-800 text-xs">
                  <h4 className="font-bold flex items-center gap-1.5 mb-2">
                    <AlertTriangle size={14} />
                    Advertencias de Validación
                  </h4>
                  <ul className="list-disc pl-5 space-y-1">
                    {missingDays.length > 0 && <li>Días del mes no encontrados en el archivo: {missingDays.join(", ")}</li>}
                    {emptyDays.length > 0 && <li>Días con 0 farmacias asignadas: {emptyDays.join(", ")}</li>}
                    {missingPharmacies.size > 0 && (
                      <li>Farmacias que no coinciden con el catálogo (podrían crearse nuevas entradas o quedar sin datos): {Array.from(missingPharmacies).join(", ")}</li>
                    )}
                  </ul>
                </div>
              )}

              {error && (
                <div className="p-3 bg-red-50 text-red-700 text-xs rounded-lg border border-red-200">
                  {error}
                </div>
              )}

              <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                <div className="max-h-60 overflow-y-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-100 text-slate-600 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-2 font-bold uppercase tracking-wider text-[10px]">Día</th>
                        <th className="px-4 py-2 font-bold uppercase tracking-wider text-[10px]">Cantidad</th>
                        <th className="px-4 py-2 font-bold uppercase tracking-wider text-[10px]">Farmacias</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {parsedData.map((row, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-4 py-2 font-medium whitespace-nowrap text-slate-800">{row.day}</td>
                          <td className="px-4 py-2">
                            <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${row.pharmacies.length > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                              {row.pharmacies.length}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-slate-600 font-mono notranslate" translate="no">
                            {row.pharmacies.join(", ") || <span className="text-slate-400 italic">Ninguna</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {parsedData && (
          <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-between items-center">
            <button
              onClick={() => setParsedData(null)}
              className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 transition-colors"
            >
              Atrás
            </button>
            <button
              onClick={() => setShowConfirmModal(true)}
              disabled={loading}
              className="px-5 py-2.5 bg-emerald-500 text-white font-bold text-xs rounded-lg hover:bg-emerald-600 disabled:opacity-50 transition-colors shadow-sm flex items-center gap-1.5"
            >
              {loading ? "Subiendo..." : (
                <>
                  <Check size={14} />
                  Aprobar y Cargar Calendario
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {showConfirmModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden flex flex-col">
            <div className="p-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
              <h3 className="font-bold text-sm text-slate-800 uppercase flex items-center gap-2">
                Confirmar Publicación
              </h3>
              <button 
                onClick={() => setShowConfirmModal(false)}
                className="text-slate-400 hover:text-slate-600 bg-white rounded-full p-1 border border-slate-200"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-slate-600">
                Selecciona el mes y año en los que se publicará este calendario. Asegúrate de que coincidan con los días que procesaste.
              </p>
              
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <label className="block text-[10px] uppercase font-bold text-slate-500 font-mono mb-1">Mes</label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg p-2 text-sm text-slate-800 bg-white"
                  >
                    {["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"].map((m) => (
                      <option key={m} value={m}>
                        {m} - {new Date(2000, parseInt(m) - 1, 1).toLocaleString("es", { month: "long" }).toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-[10px] uppercase font-bold text-slate-500 font-mono mb-1">Año</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg p-2 text-sm text-slate-800 bg-white"
                  >
                    {[2024, 2025, 2026, 2027, 2028].map((y) => (
                      <option key={y} value={y.toString()}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  uploadParsedData();
                }}
                className="px-4 py-2 bg-emerald-500 text-white font-bold text-xs rounded-lg hover:bg-emerald-600 transition-colors shadow-sm flex items-center gap-1.5"
              >
                <Check size={14} />
                Publicar Ahora
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
